import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAccess } from '../contexts/AccessContext';
import { 
  Menu, X, Info, Sparkles, Music, BarChart3, Users, 
  Radio, Mic2, ListMusic, Clock, Mail, Settings, BarChart2,
  Home, LogOut, User, ChevronDown, CreditCard, TrendingUp, Briefcase
} from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import TrialCountdownPill from './TrialCountdownPill';
import storage from '../utils/storage';

const TopNavBar = ({ isAuthenticated, currentUser, onLogout }) => {
  const { t } = useLanguage();
  const { hasFeatureAccess, hasFullAccess } = useAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  
  // Check if user is admin
  const isAdmin = isAuthenticated && currentUser?.role === 'admin';
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      storage.removeItem('token');
      storage.removeItem('user');
      window.location.href = '/about';
    }
  };
  
  // All navigation items - using translation keys
  // All items are visible to everyone. Access control happens on click.
  const navItems = [
    { path: '/about', labelKey: 'navInfo', icon: Info },
    { path: '/on-air-content', labelKey: 'navOnAirContent', icon: Sparkles },
    { path: '/song-info', labelKey: 'navSongFinder', icon: Music },
    { path: '/metritis-kampanias', labelKey: 'navCampaignCalculator', icon: BarChart3 },
    { path: '/metritis-akroaton', labelKey: 'navListenersCalculator', icon: Users },
    { path: '/radio-chart', label: "The People's Chart", icon: Radio, isPro: true },
    { path: '/top-artists/greek', label: 'Top Artists', icon: TrendingUp },
    
    // Compare Stations - PRO feature for station comparison
    { path: '/compare-stations', label: 'Compare Stations', icon: BarChart3, isPro: true, requiresFeature: 'compare_stations' },
    // Radio Top Songs - visible to everyone, access checked on click
    { path: '/radio-top-songs', label: 'Radio Top Songs', icon: TrendingUp, isPro: true, requiresFeature: 'radio_top_songs' },
    { path: '/our-stations', labelKey: 'navStations', icon: Mic2 },
    // v2.13.53.1 — Removed from menu per operator request:
    //   - /playlist-analyzer
    //   - /daypart-analyzer
    // Songs Analyzer with PRO badge
    { path: '/songs-analyzer', labelKey: 'navSongsAnalyzer', icon: BarChart2, isPro: true },
    { path: '/services', labelKey: 'navServices', icon: Briefcase, isPro: true, proColor: 'red' },
    { path: '/contact', labelKey: 'navContact', icon: Mail },
    // Admin Dashboard - only visible to admins (admin menu should remain hidden for non-admins)
    ...(isAdmin ? [
      { path: '/admin', labelKey: 'navAdmin', icon: Settings, adminOnly: true },
      { path: '/admin/ai-diagnostics', label: 'AI Diagnostics', icon: Sparkles, adminOnly: true },
    ] : []),
    // Packets - at the end, highlighted yellow
    { path: '/pricing', labelKey: 'navPackets', icon: CreditCard, highlight: true },
  ];

  // Handle menu item click - check access and redirect if needed
  const handleMenuItemClick = (e, item) => {
    // Pricing page is always accessible
    if (item.path === '/pricing' || item.path === '/syndromes' || item.path === '/paketa') {
      setIsMenuOpen(false);
      return;
    }
    
    // v2.13.53.1 — Public pages accessible without login. `/services`
    // is intentionally INCLUDED here because the marketing services
    // page must be browsable by visitors. Previously, non-logged
    // visitors hitting "Services" were silently rewritten to /about
    // by the catch-all redirect below.
    const publicPaths = [
      '/about', '/on-air-content', '/song-info',
      '/our-stations', '/contact', '/services',
    ];
    if (publicPaths.includes(item.path)) {
      setIsMenuOpen(false);
      return;
    }
    
    // Non-logged users: redirect to external about page
    if (!isAuthenticated) {
      e.preventDefault();
      setIsMenuOpen(false);
      window.location.href = "https://radio-apps.online/about";
      return;
    }
    
    // If item requires a feature, check access
    if (item.requiresFeature) {
      const hasAccess = isAdmin || hasFullAccess?.() || hasFeatureAccess?.(item.requiresFeature);
      if (!hasAccess) {
        e.preventDefault();
        setIsMenuOpen(false);
        navigate('/pricing');
        return;
      }
    }
    setIsMenuOpen(false);
  };

  const isActive = (path) => {
    if (path === '/about') return location.pathname === '/about' || location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Navigation Bar - STICKY */}
      <nav 
        className="bg-gradient-to-r from-[#2D0C4B] via-[#4A1D7A] to-[#2D0C4B] border-b border-[#6B3FA0]/30 shadow-lg sticky top-0 z-50"
        data-testid="top-nav-bar"
      >
        <div className="max-w-full mx-auto px-3">
          <div className="flex items-center justify-between py-2">
            {/* Left: Logo + Hamburger Menu */}
            <div className="flex items-center gap-3" ref={menuRef}>
              {/* Hamburger Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                data-testid="hamburger-menu"
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                <span className="text-sm font-medium">Menu</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isMenuOpen && (
                <div className="absolute top-full left-3 mt-1 w-64 bg-[#2D0C4B] border border-[#6B3FA0]/50 rounded-xl shadow-2xl py-2 z-[10000]" style={{ marginTop: '8px' }}>
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    const isHighlight = item.highlight;
                    const isPro = item.isPro;
                    const isNew = item.isNew;
                    const proColor = item.proColor; // 'red' for special pulsing badge
                    const label = item.label || t(item.labelKey);
                    const requiresFeature = item.requiresFeature;
                    
                    // Check if item is locked (requires feature but user doesn't have access)
                    const isLocked = requiresFeature && !isAdmin && !hasFullAccess?.() && !hasFeatureAccess?.(requiresFeature);
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={(e) => handleMenuItemClick(e, item)}
                        className={`
                          flex items-center gap-3 px-4 py-2.5 text-sm
                          transition-colors
                          ${isHighlight && !active
                            ? 'bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20'
                            : isPro && proColor === 'red' && !active
                              ? 'bg-red-500/10 text-red-200 hover:bg-red-500/20'
                              : isPro && !active
                                ? 'bg-teal-500/10 text-teal-300 hover:bg-teal-500/20'
                                : isNew && !active
                                  ? 'bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                                  : active 
                                    ? 'bg-white/20 text-white' 
                                    : 'text-white/80 hover:text-white hover:bg-white/10'
                          }
                        `}
                        data-testid={`nav-link-${item.path.replace('/', '') || 'home'}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{label}</span>
                        {isPro && (
                          <span
                            className={
                              proColor === 'red'
                                ? 'ml-auto text-[10px] bg-gradient-to-r from-red-600 to-rose-500 text-white px-2 py-0.5 rounded-full font-bold animate-pulse shadow-lg shadow-red-500/60'
                                : 'ml-auto text-[10px] bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-2 py-0.5 rounded-full font-bold shadow-lg shadow-teal-500/50'
                            }
                          >
                            PRO
                          </span>
                        )}
                        {isLocked && (
                          <span className="ml-1 text-[10px] text-white/50" title="Απαιτείται συνδρομή">
                            🔒
                          </span>
                        )}
                        {isNew && !isPro && (
                          <span className="ml-auto text-[10px] bg-gradient-to-r from-emerald-500 to-cyan-500 text-white px-2 py-0.5 rounded-full font-bold animate-pulse shadow-lg shadow-emerald-500/50">
                            NEW
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Center: Logo/Title */}
            <Link 
              to="/about" 
              className="text-white font-bold text-lg hidden sm:block"
            >
              Radio Solutions
            </Link>
            
            {/* Right: User Controls */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Trial countdown pill — visible only when access_type === 'trial' */}
              <TrialCountdownPill />

              {/* Language Switcher - Always visible */}
              <LanguageSwitcher />
              
              {/* Home Button - Hidden on mobile */}
              <button
                onClick={() => navigate('/about')}
                className="hidden sm:flex p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title="Home"
              >
                <Home className="w-5 h-5" />
              </button>
              
              {isAuthenticated && currentUser ? (
                <>
                  {/* User Info */}
                  <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-white/10 rounded-lg text-sm">
                    <User className="w-4 h-4 text-white/70" />
                    <span className="text-white font-medium">
                      {currentUser.username || currentUser.email?.split('@')[0]}
                    </span>
                    {isAdmin && (
                      <span className="text-[10px] bg-purple-500 text-white px-1.5 rounded">
                        admin
                      </span>
                    )}
                  </div>
                  
                  {/* Logout Button */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    title={t('logout') || 'Αποσύνδεση'}
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">Exit</span>
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="px-3 py-2 text-sm text-white bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors"
                >
                  {t('login') || 'Σύνδεση'}
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default TopNavBar;
